// Minimal, dependency-free XLSX (OOXML SpreadsheetML) writer.
//
// Why hand-rolled instead of a library (SheetJS/exceljs): this app has no
// build-time access from this workflow to add an npm dependency, and the
// export only needs flat "array of rows" sheets — a small, self-contained
// writer covers that without adding anything to package.json. It produces a
// real .xlsx (a zip of OOXML parts, STORE/no-compression method — valid per
// the zip spec and readable by Excel/Sheets/Numbers without needing deflate),
// not a CSV-with-an-xlsx-extension trick.
//
// Usage:
//   downloadXlsx("retention.xlsx", [
//     { name: "Feedback", columns: [{ header: "Date", key: "date" }, ...], rows: [...] },
//     { name: "Summary", columns: [...], rows: [...] },
//   ]);
//
// Column format hints (optional, per column): "percent" (fraction 0-1 ->
// "12.3%"), "currency" (-> "$1,234"). Anything else renders as a plain
// number or string with Excel's General format.
//
// downloadSectionedXlsx is the single-worksheet counterpart: instead of one
// worksheet per table, every section (its own title + columns + rows) is
// stacked vertically inside ONE worksheet, in the order given, separated by
// a blank row. This is what each page tab's "Download XLSX" button uses so
// everything currently on that tab lands together in one sheet, matching
// what's on screen, rather than being split across several worksheet tabs.
//
//   downloadSectionedXlsx("financial-review-2026-07.xlsx", "Financial Review", [
//     { title: "Summary", columns: [{ header: "Licensed", key: "licensed" }, ...], rows: [...] },
//     { title: "By VISN", columns: [...], rows: [...] },
//   ]);

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function textToBytes(str) {
  return new TextEncoder().encode(str);
}

// XML 1.0 forbids most control characters outright (only tab/LF/CR are
// legal below 0x20) — not just the markup characters below. Free-form text
// fields (e.g. retention feedback comments) can carry them from a paste or
// an odd export, and an unescaped one produces a worksheet XML readers
// consider malformed, which Excel/Sheets/Numbers can respond to by
// "repairing" the file and silently dropping the offending cell. Stripped
// rather than escaped since there's no valid XML escape for them.
const XML_INVALID_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F]/g;

function escapeXml(value) {
  return String(value)
    .replace(XML_INVALID_CHARS, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// 0-based column index -> "A", "B", ..., "Z", "AA", ...
function colName(index) {
  let n = index + 1;
  let name = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

const STYLE_DEFAULT = 0;
const STYLE_HEADER = 1;
const STYLE_PERCENT = 2;
const STYLE_CURRENCY = 3;
const STYLE_SECTION_TITLE = 4;

function styleForColumn(column) {
  if (column.format === "percent") return STYLE_PERCENT;
  if (column.format === "currency") return STYLE_CURRENCY;
  return STYLE_DEFAULT;
}

function cellXml(colIndex, rowNumber, value, styleIndex) {
  const ref = `${colName(colIndex)}${rowNumber}`;
  const sAttr = styleIndex ? ` s="${styleIndex}"` : "";
  if (value === null || value === undefined || value === "") {
    return `<c r="${ref}"${sAttr}/>`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${ref}"${sAttr}><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${value ? "Yes" : "No"}</t></is></c>`;
  }
  return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function sheetXml(columns, rows) {
  const headerRow = `<row r="1">${columns
    .map((col, i) => cellXml(i, 1, col.header, STYLE_HEADER))
    .join("")}</row>`;
  const dataRows = rows
    .map((row, rIdx) => {
      const r = rIdx + 2;
      const cells = columns
        .map((col, cIdx) => cellXml(cIdx, r, row[col.key], styleForColumn(col)))
        .join("");
      return `<row r="${r}">${cells}</row>`;
    })
    .join("");
  const lastCol = colName(Math.max(columns.length - 1, 0));
  const dimensionRef = `A1:${lastCol}${rows.length + 1}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="${dimensionRef}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetData>${headerRow}${dataRows}</sheetData>
</worksheet>`;
}

// Same cell-writing building blocks as sheetXml, but lays multiple
// { title, columns, rows } sections out one after another down a SINGLE
// worksheet instead of putting each on its own worksheet: a bold section
// title row (when given), a header row, then that section's data rows,
// then one blank row before the next section starts. Row numbers for the
// blank spacer are simply skipped rather than written — sheetData doesn't
// require every row to be present.
function sectionedSheetXml(sections) {
  const rowXmls = [];
  let rowNum = 1;
  let maxCols = 1;

  for (const section of sections) {
    maxCols = Math.max(maxCols, section.columns.length);

    if (section.title) {
      rowXmls.push(`<row r="${rowNum}">${cellXml(0, rowNum, section.title, STYLE_SECTION_TITLE)}</row>`);
      rowNum++;
    }

    rowXmls.push(
      `<row r="${rowNum}">${section.columns
        .map((col, i) => cellXml(i, rowNum, col.header, STYLE_HEADER))
        .join("")}</row>`
    );
    rowNum++;

    for (const row of section.rows) {
      const cells = section.columns
        .map((col, i) => cellXml(i, rowNum, row[col.key], styleForColumn(col)))
        .join("");
      rowXmls.push(`<row r="${rowNum}">${cells}</row>`);
      rowNum++;
    }

    rowNum++; // blank spacer row before the next section
  }

  const lastCol = colName(maxCols - 1);
  const lastRow = Math.max(rowNum - 2, 1);
  const dimensionRef = `A1:${lastCol}${lastRow}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="${dimensionRef}"/>
<sheetViews><sheetView workbookViewId="0"/></sheetViews>
<sheetData>${rowXmls.join("")}</sheetData>
</worksheet>`;
}

function contentTypesXml(sheetCount) {
  const overrides = Array.from({ length: sheetCount }, (_, i) =>
    `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  ).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${overrides}
</Types>`;
}

function rootRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

function workbookXml(sheets) {
  const sheetEls = sheets
    .map((s, i) => `<sheet name="${escapeXml(s.name).slice(0, 31)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${sheetEls}</sheets>
</workbook>`;
}

function workbookRelsXml(sheetCount) {
  const sheetRels = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
  ).join("");
  const stylesRel = `<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheetRels}${stylesRel}
</Relationships>`;
}

function stylesXml() {
  // numFmtId 164/165 are in the "custom" range (>=164) reserved by the spec
  // for user-defined formats; 0-163 are Excel's built-ins.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2">
<numFmt numFmtId="164" formatCode="0.0%"/>
<numFmt numFmtId="165" formatCode="&quot;$&quot;#,##0"/>
</numFmts>
<fonts count="3">
<font><sz val="11"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><name val="Calibri"/></font>
<font><b/><sz val="13"/><name val="Calibri"/></font>
</fonts>
<fills count="2">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
</fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
}

// --- Minimal STORE-method zip writer (no compression — valid per the zip
// spec, avoids needing a deflate implementation for a client-side export). ---

function dosDateTime(date) {
  const time =
    ((date.getHours() & 0x1f) << 11) | ((date.getMinutes() & 0x3f) << 5) | ((date.getSeconds() >> 1) & 0x1f);
  const dosDate =
    (((date.getFullYear() - 1980) & 0x7f) << 9) | (((date.getMonth() + 1) & 0xf) << 5) | (date.getDate() & 0x1f);
  return { time, dosDate };
}

function buildZip(files) {
  const { time, dosDate } = dosDateTime(new Date());
  const chunks = [];
  const centralChunks = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = textToBytes(file.name);
    const data = file.data;
    const crc = crc32(data);
    const size = data.length;

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, 0, true); // flags
    local.setUint16(8, 0, true); // method: store
    local.setUint16(10, time, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true); // extra length

    chunks.push(new Uint8Array(local.buffer), nameBytes, data);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true); // version made by
    central.setUint16(6, 20, true); // version needed
    central.setUint16(8, 0, true); // flags
    central.setUint16(10, 0, true); // method: store
    central.setUint16(12, time, true);
    central.setUint16(14, dosDate, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, nameBytes.length, true);
    central.setUint16(30, 0, true); // extra length
    central.setUint16(32, 0, true); // comment length
    central.setUint16(34, 0, true); // disk number start
    central.setUint16(36, 0, true); // internal attrs
    central.setUint32(38, 0, true); // external attrs
    central.setUint32(42, offset, true); // relative offset of local header

    centralChunks.push(new Uint8Array(central.buffer), nameBytes);

    offset += 30 + nameBytes.length + size;
  }

  const centralSize = centralChunks.reduce((sum, c) => sum + c.length, 0);
  const centralOffset = offset;

  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(4, 0, true);
  eocd.setUint16(6, 0, true);
  eocd.setUint16(8, files.length, true);
  eocd.setUint16(10, files.length, true);
  eocd.setUint32(12, centralSize, true);
  eocd.setUint32(16, centralOffset, true);
  eocd.setUint16(20, 0, true);

  const totalSize = offset + centralSize + 22;
  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const chunk of [...chunks, ...centralChunks, new Uint8Array(eocd.buffer)]) {
    out.set(chunk, pos);
    pos += chunk.length;
  }
  return out;
}

// Builds the raw xlsx file bytes for a list of { name, columns, rows } sheets.
// Kept separate from downloadXlsx so it can be exercised without a browser
// (no Blob/URL dependency) for verification.
export function buildXlsxBytes(sheets) {
  const usableSheets = sheets.filter((s) => s && s.rows);
  const files = [
    { name: "[Content_Types].xml", data: textToBytes(contentTypesXml(usableSheets.length)) },
    { name: "_rels/.rels", data: textToBytes(rootRelsXml()) },
    { name: "xl/workbook.xml", data: textToBytes(workbookXml(usableSheets)) },
    { name: "xl/_rels/workbook.xml.rels", data: textToBytes(workbookRelsXml(usableSheets.length)) },
    { name: "xl/styles.xml", data: textToBytes(stylesXml()) },
    ...usableSheets.map((s, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: textToBytes(sheetXml(s.columns, s.rows)),
    })),
  ];
  return buildZip(files);
}

// Triggers a browser download of the given sheets as a single .xlsx file.
// sheets: [{ name, columns: [{ header, key, format? }], rows: [{...}] }]
export function downloadXlsx(filename, sheets) {
  const bytes = buildXlsxBytes(sheets);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Builds the raw xlsx file bytes for a SINGLE worksheet made of stacked
// sections. sheetName is the workbook's one worksheet tab name (<=31 chars,
// truncated by workbookXml); sections: [{ title?, columns, rows }]. Kept
// separate from downloadSectionedXlsx for the same reason buildXlsxBytes is
// separate from downloadXlsx — exercisable without a browser.
export function buildSectionedXlsxBytes(sheetName, sections) {
  const usableSections = sections.filter((s) => s && s.rows);
  const files = [
    { name: "[Content_Types].xml", data: textToBytes(contentTypesXml(1)) },
    { name: "_rels/.rels", data: textToBytes(rootRelsXml()) },
    { name: "xl/workbook.xml", data: textToBytes(workbookXml([{ name: sheetName }])) },
    { name: "xl/_rels/workbook.xml.rels", data: textToBytes(workbookRelsXml(1)) },
    { name: "xl/styles.xml", data: textToBytes(stylesXml()) },
    { name: "xl/worksheets/sheet1.xml", data: textToBytes(sectionedSheetXml(usableSections)) },
  ];
  return buildZip(files);
}

// Triggers a browser download of the given sections as a single .xlsx file
// with exactly one worksheet, named sheetName, containing every section
// stacked vertically in order.
// sections: [{ title?, columns: [{ header, key, format? }], rows: [{...}] }]
export function downloadSectionedXlsx(filename, sheetName, sections) {
  const bytes = buildSectionedXlsxBytes(sheetName, sections);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
